import { graphql, readInlineData } from 'react-relay/legacy';
import { defineMessages, IntlShape } from 'dibs-react-intl';

import { NoteObj, Cta } from './helpers/sharedMessageHelpers';

import { TradeEOYMessaging_viewer$key } from './__generated__/TradeEOYMessaging_viewer.graphql';
import {
    TradeEOYMessaging_profile$key,
    Currency,
} from './__generated__/TradeEOYMessaging_profile.graphql';
import { TradeEOYMessaging_tradeFirmRewards$key } from './__generated__/TradeEOYMessaging_tradeFirmRewards.graphql';

export const DOWNGRADE = 'DOWNGRADE_STATUS' as const;
export const LOSE = 'LOSE_ALL_STATUS' as const;
const STATUS_SILVER = 'SILVER' as const;
const STATUS_BRONZE = 'BRONZE' as const;

const BONUS_POINTS = '10k' as const;
// output will be `$5k`
const MIN_SPEND = 5 as const;
const endDate = '12/28';

type Note = NonNullable<NoteObj['note']>;
type NoteContent = Note['content'];
type MessageContent = NoteContent & { serviceId: string };

type MessageTemplate = {
    desktop: MessageContent;
    mobile: MessageContent;
};

export type MessageConfig = {
    statusType: typeof DOWNGRADE | typeof LOSE;
    currentProgramCode: typeof STATUS_BRONZE | typeof STATUS_SILVER;
    currency: Currency;
};

const translatedMessages = defineMessages({
    learnMoreCta: {
        id: 'dbl.messageBanner.TradeEOY.learnMore',
        defaultMessage: 'Learn More.',
    },
    detailsCta: {
        id: 'dbl.messageBanner.TradeEOY.details',
        defaultMessage: 'Details.',
    },
    welcomeDesktop: {
        id: 'dbl.messageBanner.TradeEOY.welcomeDesktop',
        defaultMessage: 'Welcome to the world’s leading marketplace for extraordinary design.',
    },
    welcomeMobile: {
        id: 'dbl.messageBanner.TradeEOY.welcomeMobile',
        defaultMessage: 'Welcome to the leading marketplace for extraordinary design',
    },
    benefitsDesktop: {
        id: 'dbl.messageBanner.TradeEOY.benefitsDesktop',
        defaultMessage:
            'Experience the benefits of our Trade 1st Program, designed to help your business thrive.',
    },
    benefitsMobile: {
        id: 'dbl.messageBanner.TradeEOY.benefitsMobile',
        defaultMessage:
            'Enjoy price matching, buyer protection & trusted global delivery 1stDibs Promise',
    },
    bronzeLoseStatusDesktop: {
        id: 'dbl.messageBanner.TradeEOY.bronzeLoseStatusDesktop',
        defaultMessage:
            'Earn {points} points when you spend {price}k in total by {date} — and enjoy all the benefits of Bronze status in 2021.',
    },
    bronzeLoseStatusMobile: {
        id: 'dbl.messageBanner.TradeEOY.bronzeLoseStatusMobile',
        defaultMessage: 'Spend {price}k by {date} to enjoy Bronze benefits in 2021.',
    },
    silverLoseStatusDesktop: {
        id: 'dbl.messageBanner.TradeEOY.silverDowngradeStatusDesktop',
        defaultMessage:
            'Earn {points} points when you spend {price}k in total by {date} — and enjoy all the benefits of Bronze status in 2021.',
    },
    silverLoseStatusMobile: {
        id: 'dbl.messageBanner.TradeEOY.silverDowngradeStatusMobile',
        defaultMessage: 'Spend {price}k by {date} to enjoy member benefits in 2021.',
    },
    silverDowngradeStatusDesktop: {
        id: 'dbl.messageBanner.TradeEOY.silverLoseStatusDesktop',
        defaultMessage:
            'Earn {points} points when you spend {price}k in total by {date} — and enjoy all the benefits of Silver status in 2021.',
    },
    silverDowngradeStatusMobile: {
        id: 'dbl.messageBanner.TradeEOY.silverLoseStatusMobile',
        defaultMessage: 'Spend {points} by {date} to enjoy Silver benefits in 2021.',
    },
});

const defaultMessages = [
    (intl: IntlShape): MessageTemplate => {
        const desktopMsg = intl.formatMessage(translatedMessages.welcomeDesktop);
        const mobileMsg = intl.formatMessage(translatedMessages.welcomeMobile);
        const base = {
            serviceId: 'tradeMessage1',
            title: 'Trade Message 1',
            cta: null,
        };
        return {
            desktop: { ...base, message: desktopMsg },
            mobile: { ...base, message: mobileMsg },
        };
    },
    (intl: IntlShape): MessageTemplate => {
        const desktopMsg = intl.formatMessage(translatedMessages.benefitsDesktop);
        const mobileMsg = intl.formatMessage(translatedMessages.benefitsMobile);
        const text = intl.formatMessage(translatedMessages.learnMoreCta);
        const desktopCtaUrl = '/trade/rewards/';
        const mobileCtaUrl = '/about/promise/';
        const base = {
            serviceId: 'tradeMessage2',
            title: 'Trade Message 2',
        };
        return {
            desktop: {
                ...base,
                message: desktopMsg,
                cta: {
                    text,
                    url: desktopCtaUrl,
                },
            },
            mobile: {
                ...base,
                message: mobileMsg,
                cta: {
                    text,
                    url: mobileCtaUrl,
                },
            },
        };
    },
];

const messageValues = (
    intl: IntlShape,
    currency: Currency
): { points: string; price: string; date: string } => ({
    points: BONUS_POINTS,
    price: intl.formatNumber(MIN_SPEND, {
        currency,
        style: 'currency',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }),
    date: intl.formatDate(endDate, {
        month: 'numeric',
        day: 'numeric',
    }),
});

const detailsCta = (intl: IntlShape): Cta => ({
    text: intl.formatMessage(translatedMessages.detailsCta),
    url: '/info/trade-1st-points-promo-2020/',
});

const bronzeMessages = {
    [LOSE]: (intl: IntlShape, currency: Currency): MessageTemplate => {
        const desktopMsg = intl.formatMessage(
            translatedMessages.bronzeLoseStatusDesktop,
            messageValues(intl, currency)
        );
        const mobileMsg = intl.formatMessage(
            translatedMessages.bronzeLoseStatusMobile,
            messageValues(intl, currency)
        );
        const base = {
            serviceId: 'bronzeLoseStatus',
            title: 'Bronze lose status',
            cta: detailsCta(intl),
        };
        return {
            desktop: { ...base, message: desktopMsg },
            mobile: { ...base, message: mobileMsg },
        };
    },
} as const;

const silverMessages = {
    [DOWNGRADE]: (intl: IntlShape, currency: Currency): MessageTemplate => {
        const msgOptions = messageValues(intl, currency);
        const desktopMsg = intl.formatMessage(
            translatedMessages.silverDowngradeStatusDesktop,
            msgOptions
        );
        const mobileMsg = intl.formatMessage(
            translatedMessages.silverDowngradeStatusMobile,
            msgOptions
        );
        const base = {
            serviceId: 'silverDowngradeStatus',
            title: 'Silver downgrade status',
            cta: detailsCta(intl),
        };
        return {
            desktop: { ...base, message: desktopMsg },
            mobile: { ...base, message: mobileMsg },
        };
    },
    [LOSE]: (intl: IntlShape, currency: Currency): MessageTemplate => {
        const msgOptions = messageValues(intl, currency);
        const desktopMsg = intl.formatMessage(
            translatedMessages.silverLoseStatusDesktop,
            msgOptions
        );
        const mobileMsg = intl.formatMessage(translatedMessages.silverLoseStatusMobile, msgOptions);
        const base = {
            serviceId: 'silverLoseStatus',
            title: 'Silver lose status',
            cta: detailsCta(intl),
        };
        return {
            desktop: { ...base, message: desktopMsg },
            mobile: { ...base, message: mobileMsg },
        };
    },
} as const;

const getMessage = (message: MessageTemplate): Note => {
    const { desktop, mobile } = message;
    const { serviceId, ...content } = desktop;
    const { serviceId: mobileServiceId, ...mobileContent } = mobile;

    return {
        serviceId,
        content,
        anchors: [
            {
                serviceId: `${mobileServiceId}Anchor`,
                content: mobileContent,
            },
        ],
    };
};

const getTradeMessage = (
    intl: IntlShape,
    { statusType, currentProgramCode, currency }: MessageConfig
): NoteObj | null => {
    const eoyMessage = currentProgramCode === STATUS_BRONZE ? bronzeMessages : silverMessages;
    if (currentProgramCode === STATUS_BRONZE && statusType === LOSE) {
        const msg = eoyMessage[LOSE](intl, currency);
        return {
            note: getMessage(msg),
        };
    } else if (
        currentProgramCode === STATUS_SILVER &&
        (statusType === LOSE || statusType === DOWNGRADE)
    ) {
        const msg = (eoyMessage as typeof silverMessages)[statusType](intl, currency);
        return {
            note: getMessage(msg),
        };
    }
    return null;
};

export const getTradeEOYMessageBanner = (
    intl: IntlShape,
    config: MessageConfig | null
): (NoteObj | null)[] | null => {
    if (!config) {
        return null;
    }
    return [
        getTradeMessage(intl, config),
        { note: getMessage(defaultMessages[0](intl)) },
        { note: getMessage(defaultMessages[1](intl)) },
    ];
};

const thresholds = {
    [STATUS_BRONZE]: {
        downgradeStatus: null,
        loseAllStatus: {
            min: 0,
            max: 4999,
        },
    },
    [STATUS_SILVER]: {
        loseAllStatus: {
            min: 0,
            max: 4999,
        },
        downgradeStatus: {
            min: 15000,
            max: 19999,
        },
    },
} as const;

const featureFlagFragment = graphql`
    fragment TradeEOYMessaging_viewer on Viewer @inline {
        tradeEOY2020: featureFlag(feature: "tradeEOY2020FF")
    }
`;
// should be valid till 2020/12/28 at 11:59:59PM
const SHOW_MESSAGE_TILL_DATE = new Date('2020-12-28T23:59:59.000Z');

export const isValidDate = (): boolean => {
    return new Date() < SHOW_MESSAGE_TILL_DATE;
};

export const showTradeEOYMessage = (tradeFirmFFRef: TradeEOYMessaging_viewer$key): boolean => {
    const { tradeEOY2020 } = readInlineData(featureFlagFragment, tradeFirmFFRef);
    return !!(tradeEOY2020 && isValidDate());
};

const profileDataFragment = graphql`
    fragment TradeEOYMessaging_profile on Profile @inline {
        currency
    }
`;

const tradeFirmRewardsFragment = graphql`
    fragment TradeEOYMessaging_tradeFirmRewards on TradeFirmRewards @inline {
        currentYearPoints
        currentProgram {
            programInfo {
                code
            }
        }
    }
`;

// https://1stdibs.atlassian.net/browse/CAM-376
// Trade 2020:
// At risk of losing Bronze status → Current status = Bronze, YTD points earned = 0 - 4999
// - Bronze upgrades at 10k spend YTD
// Silver firms at risk of losing all status → Current status = Silver, YTD points earned = 0 - 4999
// Silver firms at risk of downgrading to Bronze → Current status = Silver, YTD points earned = 15k - 19.9k
// - Silver upgrades at 25k spend YTD
export const getStatusInfo = (
    profileRef: TradeEOYMessaging_profile$key,
    tradeFirmRef: TradeEOYMessaging_tradeFirmRewards$key,
    defaultCurrency: Currency
): MessageConfig | null => {
    const profileInfo = readInlineData(profileDataFragment, profileRef);
    const tradeFirm = readInlineData(tradeFirmRewardsFragment, tradeFirmRef);
    if (!profileInfo && !tradeFirm) {
        return null;
    }
    const { currentYearPoints, currentProgram } = tradeFirm || {};
    const currentProgramCode = currentProgram?.programInfo?.code;
    const baseReturnObj = {
        statusType: null,
        currency: profileInfo?.currency || defaultCurrency,
        currentProgramCode: currentProgramCode as typeof STATUS_BRONZE | typeof STATUS_SILVER,
    };
    const hasValidStatus =
        [STATUS_SILVER, STATUS_BRONZE].filter(status => status === currentProgramCode).length > 0;
    const threshold = hasValidStatus
        ? thresholds[currentProgramCode as typeof STATUS_BRONZE | typeof STATUS_SILVER]
        : null;
    if (hasValidStatus && currentYearPoints === 0) {
        return {
            ...baseReturnObj,
            statusType: LOSE,
        };
    }
    const points = Number(currentYearPoints);
    const { downgradeStatus, loseAllStatus } = threshold || {};
    if (hasValidStatus) {
        if (
            downgradeStatus &&
            points > Number(downgradeStatus.min) &&
            points < Number(downgradeStatus.max)
        ) {
            return {
                ...baseReturnObj,
                statusType: DOWNGRADE,
            };
        } else if (loseAllStatus && points < Number(loseAllStatus.max)) {
            return {
                ...baseReturnObj,
                statusType: LOSE,
            };
        }
    }

    return null;
};
